/* Admin calendar — the shared session shape and the type palette.
 *
 * Every calendar surface (Bookings → Calendar's pulse, list and full timeline,
 * and Bookings → Today's day-by-resource grid) reads this shape, so the layout
 * algorithm and the card anatomy are written once.
 *
 * Colours follow the handoff formula: one lightness/chroma for every type, and
 * a hue per type. Adding a bookable type means picking an unused hue — nothing
 * else changes. */

export type BookableType = 'class' | 'spa' | 'pt' | 'consult' | 'facility';

/** Sort order used everywhere a mixed list of types is shown. */
export const CAL_TYPE_ORDER: BookableType[] = ['class', 'pt', 'facility', 'spa', 'consult'];

/** Hues reserved for types we already ship. `event` (255) is held back for the
 *  next one so the palette doesn't have to be reshuffled. */
const TYPE_HUE: Record<BookableType, number> = {
  class: 150,
  pt: 300,
  facility: 30,
  spa: 200,
  consult: 80,
};

const TYPE_LABEL: Record<BookableType, string> = {
  class: 'Group classes',
  pt: 'Personal training',
  facility: 'Courts & facilities',
  spa: 'Spa & recovery',
  consult: 'Consultations',
};

/** Short label for tight spots (filter pills, column subtitles). */
const TYPE_SHORT: Record<BookableType, string> = {
  class: 'Classes',
  pt: 'Personal',
  facility: 'Facility',
  spa: 'Spa',
  consult: 'Consult',
};

export interface CalTypeMeta {
  key: BookableType;
  label: string;
  short: string;
  hue: number;
  /** Main colour — left border, pills, meters. */
  c: string;
  /** Card background. */
  bg: string;
  /** Half-full segment tint. */
  tint: string;
  /** Readable text on `bg`. */
  ink: string;
  /** Hairline in the type's hue. */
  line: string;
}

const meta = (key: BookableType): CalTypeMeta => {
  const h = TYPE_HUE[key];
  return {
    key,
    label: TYPE_LABEL[key],
    short: TYPE_SHORT[key],
    hue: h,
    c: `oklch(0.62 0.15 ${h})`,
    bg: `oklch(0.975 0.022 ${h})`,
    tint: `oklch(0.89 0.07 ${h})`,
    ink: `oklch(0.36 0.1 ${h})`,
    line: `oklch(0.9 0.045 ${h})`,
  };
};

export const CAL_TYPES: Record<BookableType, CalTypeMeta> = {
  class: meta('class'),
  spa: meta('spa'),
  pt: meta('pt'),
  consult: meta('consult'),
  facility: meta('facility'),
};

export const calType = (t?: string | null): CalTypeMeta =>
  CAL_TYPES[(t || 'class') as BookableType] || CAL_TYPES.class;

/** The CSS custom properties a card/rail/segment reads for its type colour. */
export const typeVars = (T: CalTypeMeta): React.CSSProperties =>
  ({
    '--c': T.c,
    '--tbg': T.bg,
    '--tink': T.ink,
    '--line': T.line,
    '--tint': T.tint,
  }) as React.CSSProperties;

/** Heat ramp for the week pulse. Step 0 is plain white — "nothing here". */
export const HEAT_RAMP = [
  '#FFFFFF',
  'oklch(0.975 0.03 125)',
  'oklch(0.945 0.07 125)',
  'oklch(0.9 0.12 125)',
  'oklch(0.85 0.16 125)',
  'oklch(0.77 0.18 128)',
];

export const heatLevel = (v: number): number => (v <= 0 ? 0 : Math.min(5, 1 + Math.floor(v * 4.999)));

// ─── Session shape ───────────────────────────────────────────────────────────

/** One occurrence, flattened from a ClassSession for the calendar surfaces. */
export interface CalSession {
  id: string;
  /** Bookable + resource identity. Back-to-back slots sharing it merge into a run. */
  bk: string;
  name: string;
  type: BookableType;
  /** Studio, court, therapy room — or the staff member, when they are the resource. */
  res: string;
  /** Coach / trainer / therapist. */
  who: string | null;
  /** For 1:1 bookables, the member holding the slot. */
  member: string | null;
  branchId: string;
  branch: string;
  /** Calendar date, YYYY-MM-DD. */
  date: string;
  /** 0–6, Monday-first, within the week being displayed. -1 when off-week. */
  day: number;
  /** Minutes from midnight. */
  start: number;
  end: number;
  cap: number;
  booked: number;
  wait: number;
  /** A pre-generated bookable unit (court hour, pool block) rather than a scheduled class. */
  slot: boolean;
  status: 'ok' | 'cancelled';
  past: boolean;
  live: boolean;
  /** Extra flags the admin cards surface. */
  sourceMode?: string;
  /** Does this bookable expect a person to deliver it? A court does not; a
   *  class, a massage or a PT hour does. Splits the staff axis's "Unassigned"
   *  column (a class with no coach — a problem) from "Self-serve" (a court —
   *  working as intended). */
  staffed: boolean;
  /** Booked, staffed and nobody assigned — the card's amber flag. */
  needsStaff: boolean;
  /** Members through the door, when the query asked for check-ins. */
  checkedIn: number;
  /** The row this was built from, for the detail panel and edit flows. */
  raw: unknown;
}

/** A session, or a merged run of back-to-back slots of the same bookable. */
export type CalItem =
  | { kind: 'one'; id: string; start: number; end: number; type: BookableType; s: CalSession }
  | { kind: 'run'; id: string; start: number; end: number; type: BookableType; s: CalSession; bk: string; day: number; slots: CalSession[] }
  | { kind: 'more'; id: string; start: number; end: number; items: CalItem[] };

export const isMore = (i: CalItem): i is Extract<CalItem, { kind: 'more' }> => i.kind === 'more';
